import React from 'react';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Row, Column } from 'components/shared/Layout';
import FormattedCurrency from 'components/ui/FormattedCurrency';

import { VerticalLine, StyledPrice, StyledHr, LastColumn } from '../Extras.styles';
import { HOTEL_BOOKING } from 'store/queries';

const HOTEL_ROOMS_KEY = 'hotel_rooms';

const Hotel = ({ hotel, children, additionalDetails = [] }) => {
  const { loading, data } = useQuery(HOTEL_BOOKING, {
    variables: {
      reference: hotel.eurostarReference
    },
    skip: Boolean(hotel.jacTravelRef)
  });

  if (loading) {
    return null;
  }

  const jacTravelRef = hotel.jacTravelRef || data.hotelBooking?.bookingCode;

  // Temporary
  const { deprecatedTrainMargin = 0, margin = 0 } = hotel;

  const bookingHotelRooms = JSON.parse(
    additionalDetails.find(({ itemRef, key }) => itemRef === hotel.itemId && key === HOTEL_ROOMS_KEY)?.value ?? 'null'
  );

  return (
    <div key={jacTravelRef} data-cy="extras-hotel" data-testid="extras-hotel">
      <Row>
        <Column span={4}>
          <VerticalLine>
            <strong>
              <FormattedMessage id="extras.passenger" />
            </strong>
            <div>
              {hotel.passenger.firstName} {hotel.passenger.lastName}
            </div>
          </VerticalLine>
        </Column>
        <Column span={3}>
          <VerticalLine>
            <strong>
              <FormattedMessage id="extras.hotel.checkInDate" />
            </strong>
            <div>{hotel.checkInDate}</div>
          </VerticalLine>
        </Column>
        <Column span={3}>
          <VerticalLine>
            <strong>
              <FormattedMessage id="extras.hotel.jacTravelReference" />
            </strong>
            <div>{jacTravelRef}</div>
          </VerticalLine>
        </Column>
        <Column span={3}>
          <VerticalLine>
            <strong>
              <FormattedMessage id="extras.hotel.id" />
            </strong>
            <div>{hotel.hotelId}</div>
          </VerticalLine>
        </Column>
        <Column span={3}>
          <LastColumn>
            <strong>
              <FormattedMessage id="extras.hotel.eurostarPackageRef" />
            </strong>
            <div>{hotel.eurostarReference}</div>
          </LastColumn>
        </Column>
      </Row>

      <StyledHr />

      <Row>
        <Column span={4}>
          <VerticalLine>
            <strong>
              <FormattedMessage id="extras.hotel.hotelName" />
            </strong>
            <div>{hotel.hotelName || '-'}</div>
          </VerticalLine>
        </Column>
        <Column span={3}>
          <VerticalLine>
            <strong>
              <FormattedMessage id="extras.hotel.numberOfNights" />
            </strong>
            <div>{hotel.numberOfNights || '-'}</div>
          </VerticalLine>
        </Column>
        <Column span={3}>
          <VerticalLine>
            <strong>
              <FormattedMessage id="extras.hotel.guestCount" />
            </strong>
            <div>{hotel.guestCount || '-'}</div>
          </VerticalLine>
        </Column>
        <Column span={3}>
          <VerticalLine>
            <strong>
              <FormattedMessage id="extras.hotel.roomCount" />
            </strong>
            <div>{hotel.roomCount || '-'}</div>
          </VerticalLine>
        </Column>
        <Column span={3}>
          <LastColumn>
            <strong>
              <FormattedMessage id="extras.hotel.hotelAddress" />
            </strong>
            <div>{hotel.hotelAddress || '-'}</div>
          </LastColumn>
        </Column>
      </Row>

      <StyledHr />

      {bookingHotelRooms != null && !!bookingHotelRooms?.length ? (
        <>
          <Row>
            <Column span={16}>
              <LastColumn>
                <strong>
                  <FormattedMessage id="extras.hotel.roomsDetails" />
                </strong>
              </LastColumn>
            </Column>
          </Row>
          {bookingHotelRooms.map((room, index) => (
            <Row key={`${index}_${room?.roomType}_${room?.guests?.length}`}>
              <Column span={3}>
                <LastColumn>
                  <strong>
                    <FormattedMessage id="extras.hotel.roomType" />
                  </strong>
                  <div>{room?.roomType || '-'}</div>
                </LastColumn>
              </Column>
              <Column span={3}>
                <LastColumn>
                  <strong>
                    <FormattedMessage id="extras.hotel.guestCount" />
                  </strong>
                  <div>{room?.guests?.length || '-'}</div>
                </LastColumn>
              </Column>
              <Column span={3}>
                <LastColumn>
                  <strong>
                    <FormattedMessage id="extras.hotel.roomBoardBasis" />
                  </strong>
                  <div>{room?.eurostarMealBasisName || '-'}</div>
                </LastColumn>
              </Column>
            </Row>
          ))}
          <StyledHr />
        </>
      ) : null}

      <Row span={16}>
        <StyledPrice>
          <FormattedMessage id="extras.hotel.modal.price" />:{' '}
          <FormattedCurrency currency={hotel.currency} amount={hotel.priceWithMargin - margin - deprecatedTrainMargin} />
        </StyledPrice>
      </Row>
      <Row span={16}>
        <StyledPrice>
          <FormattedMessage id="extras.hotel.margin" />: <FormattedCurrency currency={hotel.currency} amount={hotel.margin} />
        </StyledPrice>
      </Row>
      <Row span={16}>
        <StyledPrice>
          <FormattedMessage id="extras.total" />:{' '}
          <FormattedCurrency currency={hotel.currency} amount={hotel.priceWithMargin - deprecatedTrainMargin} />
        </StyledPrice>
      </Row>

      {children ? React.cloneElement(children, { jacTravelRef }) : null}
    </div>
  );
};

Hotel.propTypes = {
  hotel: PropTypes.shape({
    cancelled: PropTypes.bool,
    passenger: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string
    }),
    checkInDate: PropTypes.string,
    jacTravelRef: PropTypes.string,
    hotelId: PropTypes.string,
    eurostarReference: PropTypes.string,
    currency: PropTypes.string,
    price: PropTypes.number,
    deprecatedTrainMargin: PropTypes.number,
    margin: PropTypes.number,
    priceWithMargin: PropTypes.number,
    itemRef: PropTypes.string.isRequired,
    itemId: PropTypes.string.isRequired,
    bookerSurname: PropTypes.string.isRequired,
    hotelName: PropTypes.string,
    hotelAddress: PropTypes.string,
    numberOfNights: PropTypes.number,
    guestCount: PropTypes.number,
    roomCount: PropTypes.number
  }),
  additionalDetails: PropTypes.arrayOf(
    PropTypes.shape({
      itemRef: PropTypes.string,
      key: PropTypes.string,
      value: PropTypes.string
    })
  ),
  children: PropTypes.node
};

export default Hotel;
